// Options for the side pane's text-label pickers (Name field "Link text label",
// Description "Link description label"). A drawing often has several labels
// with the same text — a tag next to its pump and the same tag again where a
// line heads to it — so each option carries what it's already linked to, and
// the list is ordered by distance from the item being edited: the label you
// want is almost always the one sitting next to it.
import { getLabelForType } from '../data/equipmentTypes';
import { nearestPointOnPolyline } from './geometry';

const ANNOTATION_RELATIONSHIPS = ['names', 'describes', 'labels'];
const VERB = { names: 'names', labels: 'names', describes: 'describes' };

const idOf = (v) => (v && typeof v === 'object' ? String(v._id) : v != null ? String(v) : null);

const centre = (bbox) => ({ x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 });

// Distance from a label to the item being named/described: centre to centre
// for an element; to the nearest point on its route for a pipe (connection).
function distanceToTarget(label, target, targetType, elementsById) {
  if (!label.boundingBox) return Infinity;
  const p = centre(label.boundingBox);
  if (targetType === 'Connection') {
    let segments = target.metadata?.path_data?.path;
    if (!Array.isArray(segments) || segments.length === 0) {
      const a = elementsById.get(idOf(target.source))?.boundingBox;
      const b = elementsById.get(idOf(target.target))?.boundingBox;
      if (!a || !b) return Infinity;
      const ca = centre(a);
      const cb = centre(b);
      segments = [{ start: [ca.x, ca.y], end: [cb.x, cb.y] }];
    }
    const q = nearestPointOnPolyline(p, segments);
    return q ? Math.hypot(q.x - p.x, q.y - p.y) : Infinity;
  }
  if (!target.boundingBox) return Infinity;
  const c = centre(target.boundingBox);
  return Math.hypot(c.x - p.x, c.y - p.y);
}

// "names Centrifugal Pump PA104", "describes pipe 3"-CS-101 +1 more", or "not linked".
export function summarizeLabelLinks(label, connections, elementsById, connectionsById) {
  const links = connections.filter(c => (
    !c.metadata?.rejected && ANNOTATION_RELATIONSHIPS.includes(c.relationshipType) && idOf(c.source) === String(label._id)
  ));
  if (links.length === 0) return 'not linked';
  const describe = (c) => {
    const tid = idOf(c.target);
    const thing = c.targetType === 'Connection'
      ? `pipe${connectionsById.get(tid)?.name ? ` ${connectionsById.get(tid).name}` : ''}`
      : (() => {
          const el = elementsById.get(tid);
          return el ? `${getLabelForType(el.elementType)}${el.name ? ` ${el.name}` : ''}` : 'a deleted item';
        })();
    return `${VERB[c.relationshipType]} ${thing}`;
  };
  return links.length === 1 ? describe(links[0]) : `${describe(links[0])} +${links.length - 1} more`;
}

// Rejected labels are left out (soft-deleted). `nearby` marks labels close
// enough to plausibly belong to the target: within a few target-sizes of it.
export function rankTextLabelOptions({ elements, connections, target, targetType, excludeIds = [] }) {
  const elementsById = new Map(elements.map(el => [String(el._id), el]));
  const connectionsById = new Map(connections.map(c => [String(c._id), c]));
  const exclude = new Set(excludeIds.map(String));
  const size = targetType === 'Connection' || !target.boundingBox
    ? 60
    : Math.max(target.boundingBox.width, target.boundingBox.height);
  const nearbyWithin = Math.max(4 * size, 120);

  return elements
    .filter(el => el.elementType === 'text_label' && !el.metadata?.rejected && !exclude.has(String(el._id)) && String(el._id) !== String(target._id))
    .map(label => {
      const distance = distanceToTarget(label, target, targetType, elementsById);
      return {
        label,
        distance,
        nearby: distance <= nearbyWithin,
        links: summarizeLabelLinks(label, connections, elementsById, connectionsById),
      };
    })
    .sort((a, b) => a.distance - b.distance);
}
